package br.com.vvrefeicoes.portal.feature.guides

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.graphics.painter.BitmapPainter
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.PdfImage
import br.com.vvrefeicoes.portal.core.util.PdfWriter
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.core.util.encodeBase64
import br.com.vvrefeicoes.portal.core.util.guideShareLink
import br.com.vvrefeicoes.portal.core.util.isPdfExportSupported
import br.com.vvrefeicoes.portal.core.util.savePdf
import br.com.vvrefeicoes.portal.core.util.toPdfImage
import br.com.vvrefeicoes.portal.domain.model.Guide
import br.com.vvrefeicoes.portal.domain.model.GuideBlock
import br.com.vvrefeicoes.portal.domain.model.GuideBlockType
import br.com.vvrefeicoes.portal.domain.model.GuideDraft
import br.com.vvrefeicoes.portal.domain.model.GuideRoleRef
import br.com.vvrefeicoes.portal.domain.model.GuideStatus
import br.com.vvrefeicoes.portal.domain.model.GuideSummary
import br.com.vvrefeicoes.portal.domain.model.GuideVersion
import br.com.vvrefeicoes.portal.domain.repository.GuideRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.min

enum class GuideToast {
    ACTIVATED,
    DEACTIVATED,
    DELETED,
    PROFILES_SAVED,
}

class GuidesController(
    private val repository: GuideRepository,
    private val scope: CoroutineScope,
) {
    private var initialized = false
    private var job: Job? = null

    var loading by mutableStateOf(true)
        private set

    var items by mutableStateOf<List<GuideSummary>>(emptyList())
        private set

    var query by mutableStateOf("")
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var deleteTarget by mutableStateOf<GuideSummary?>(null)
        private set

    var deleting by mutableStateOf(false)
        private set

    var toggleTarget by mutableStateOf<GuideSummary?>(null)
        private set

    var toggling by mutableStateOf(false)
        private set

    var toast by mutableStateOf<GuideToast?>(null)
        private set

    fun dismissToast() {
        toast = null
    }

    fun init() {
        if (initialized) return
        initialized = true
        load()
    }

    fun updateQuery(value: String) {
        query = value
        load(debounce = true)
    }

    fun refresh() {
        load()
    }

    fun dismissError() {
        errorMessage = null
    }

    fun requestDelete(item: GuideSummary) {
        deleteTarget = item
    }

    fun dismissDelete() {
        deleteTarget = null
    }

    fun confirmDelete() {
        val target = deleteTarget ?: return
        scope.launch {
            deleting = true
            try {
                repository.delete(target.id)
                deleteTarget = null
                toast = GuideToast.DELETED
                load()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível excluir o guia."
            } finally {
                deleting = false
            }
        }
    }

    fun requestToggle(item: GuideSummary) {
        if (item.status != GuideStatus.PUBLICADO) return
        toggleTarget = item
    }

    fun dismissToggle() {
        toggleTarget = null
    }

    fun confirmToggle() {
        val target = toggleTarget ?: return
        if (toggling) return
        scope.launch {
            toggling = true
            errorMessage = null
            try {
                val activating = !target.active
                repository.setActive(target.id, activating)
                toggleTarget = null
                toast = if (activating) GuideToast.ACTIVATED else GuideToast.DEACTIVATED
                load()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                toggleTarget = null
                errorMessage = throwable.message ?: "Não foi possível alterar o status do manual."
            } finally {
                toggling = false
            }
        }
    }

    fun downloadPdf(
        id: String,
        assets: GuidePdfAssets,
    ) {
        if (!isPdfExportSupported()) return
        scope.launch {
            val guide = runCatching { repository.loadGuide(id) }.getOrNull() ?: return@launch
            exportGuidePdf(repository, guide, assets)
        }
    }

    private fun load(debounce: Boolean = false) {
        job?.cancel()
        job =
            scope.launch {
                if (debounce) delay(SEARCH_DEBOUNCE_MS)
                loading = true
                errorMessage = null
                try {
                    val allItems = mutableListOf<GuideSummary>()
                    var currentPage = 0
                    var pageCount = 1
                    do {
                        val result = repository.loadGuidesPage(currentPage, PAGE_SIZE, query)
                        allItems += result.content
                        pageCount = result.totalPages
                        currentPage++
                    } while (currentPage < pageCount)
                    items = allItems
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    errorMessage = throwable.message ?: "Não foi possível carregar os guias."
                } finally {
                    loading = false
                }
            }
    }

    companion object {
        const val PAGE_SIZE = 100
        const val SEARCH_DEBOUNCE_MS = 300L
    }
}

class GuideDetailController(
    private val repository: GuideRepository,
    private val guideId: String,
    private val scope: CoroutineScope,
) {
    private var initialized = false

    var loading by mutableStateOf(true)
        private set

    var guide by mutableStateOf<Guide?>(null)
        private set

    var published by mutableStateOf<Guide?>(null)
        private set

    var showingPublished by mutableStateOf(false)
        private set

    var busy by mutableStateOf(false)
        private set

    var toggling by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var deleteOpen by mutableStateOf(false)
        private set

    var versions by mutableStateOf<List<GuideVersion>>(emptyList())
        private set

    var roles by mutableStateOf<List<GuideRoleRef>>(emptyList())
        private set

    var accessOpen by mutableStateOf(false)
        private set

    var accessSaving by mutableStateOf(false)
        private set

    var accessAllRoles by mutableStateOf(true)
        private set

    var accessRoleIds by mutableStateOf<Set<String>>(emptySet())
        private set

    val effectiveAccessRoleIds: Set<String>
        get() = if (accessAllRoles) roles.map { it.id }.toSet() else accessRoleIds

    fun init() {
        if (initialized) return
        initialized = true
        load()
    }

    fun refresh() {
        load()
    }

    private fun load() {
        scope.launch {
            loading = true
            errorMessage = null
            try {
                guide = repository.loadGuide(guideId)
                published = null
                showingPublished = false
                versions = runCatching { repository.loadVersions(guideId) }.getOrDefault(emptyList())
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel carregar o guia."
            } finally {
                loading = false
            }
        }
    }

    fun viewPublished() {
        scope.launch {
            busy = true
            errorMessage = null
            try {
                published = repository.loadPublishedGuide(guideId)
                showingPublished = true
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel carregar a vers�o publicada."
            } finally {
                busy = false
            }
        }
    }

    fun viewDraft() {
        showingPublished = false
    }

    fun setActive(
        active: Boolean,
        onSuccess: (Boolean) -> Unit = {},
    ) {
        if (toggling) return
        scope.launch {
            toggling = true
            errorMessage = null
            try {
                guide = repository.setActive(guideId, active)
                onSuccess(active)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel alterar o status do manual."
            } finally {
                toggling = false
            }
        }
    }

    fun openAccess() {
        val current = guide ?: return
        accessAllRoles = current.allRoles
        accessRoleIds = current.roleIds.toSet()
        accessOpen = true
        if (roles.isEmpty()) {
            scope.launch {
                roles = runCatching { repository.loadRoles() }.getOrDefault(emptyList())
            }
        }
    }

    fun dismissAccess() {
        accessOpen = false
    }

    fun toggleAccessAll() {
        accessAllRoles = !accessAllRoles
        accessRoleIds = emptySet()
    }

    fun toggleAccessRole(id: String) {
        val current = effectiveAccessRoleIds
        val next = if (id in current) current - id else current + id
        val all = roles.map { it.id }.toSet()
        accessAllRoles = all.isNotEmpty() && next == all
        accessRoleIds = if (accessAllRoles) emptySet() else next
    }

    fun saveAccess(onSaved: () -> Unit = {}) {
        if (accessSaving) return
        scope.launch {
            accessSaving = true
            errorMessage = null
            try {
                guide =
                    repository.updateAccess(
                        id = guideId,
                        allRoles = accessAllRoles,
                        roleIds = if (accessAllRoles) emptyList() else accessRoleIds.toList(),
                    )
                accessOpen = false
                onSaved()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel atualizar os perfis de acesso."
            } finally {
                accessSaving = false
            }
        }
    }

    fun downloadPdf(
        id: String,
        assets: GuidePdfAssets,
    ) {
        if (!isPdfExportSupported()) return
        scope.launch {
            val currentGuide = guide ?: runCatching { repository.loadGuide(id) }.getOrNull() ?: return@launch
            exportGuidePdf(repository, currentGuide, assets)
        }
    }

    fun requestDelete() {
        deleteOpen = true
    }

    fun dismissDelete() {
        deleteOpen = false
    }

    fun confirmDelete(onDeleted: () -> Unit) {
        scope.launch {
            busy = true
            errorMessage = null
            try {
                repository.delete(guideId)
                deleteOpen = false
                onDeleted()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel excluir o guia."
            } finally {
                busy = false
            }
        }
    }

    fun publish(onPublished: () -> Unit) {
        scope.launch {
            busy = true
            errorMessage = null
            try {
                repository.publish(guideId)
                onPublished()
                load()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel publicar o guia."
            } finally {
                busy = false
            }
        }
    }
}

data class PendingGuideMedia(
    val bytes: ByteArray,
    val contentType: String,
    val fileName: String,
)

class GuideFormController(
    private val repository: GuideRepository,
    private val guideId: String?,
    private val scope: CoroutineScope,
) {
    private var initialized = false
    private var blockSequence = 0
    private var currentGuideId = guideId

    var loading by mutableStateOf(guideId != null)
        private set

    var saving by mutableStateOf(false)
        private set

    var deleting by mutableStateOf(false)
        private set

    var uploadingImage by mutableStateOf(false)
        private set

    var pendingMedia by mutableStateOf<Map<String, PendingGuideMedia>>(emptyMap())
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var title by mutableStateOf("")
        private set

    var description by mutableStateOf("")
        private set

    var blocks by mutableStateOf<List<GuideBlock>>(emptyList())
        private set

    var selectedRoles by mutableStateOf<Set<String>>(emptySet())
        private set

    var allRoles by mutableStateOf(false)
        private set

    var roles by mutableStateOf<List<GuideRoleRef>>(emptyList())
        private set

    var hasPublishedVersion by mutableStateOf(false)
        private set

    var isDraft by mutableStateOf(true)
        private set

    var version by mutableStateOf<Int?>(null)
        private set

    val nextVersion: Int
        get() = (version ?: 0) + 1

    val effectiveSelectedRoles: Set<String>
        get() = if (allRoles) roles.map { it.id }.toSet() else selectedRoles

    val isEditing: Boolean
        get() = guideId != null

    val canDeleteDraft: Boolean
        get() = currentGuideId != null && isDraft && !deleting

    val canSave: Boolean
        get() = title.isNotBlank() && blocks.isNotEmpty() && !saving && !deleting

    fun init() {
        if (initialized) return
        initialized = true
        scope.launch { load() }
    }

    private suspend fun load() {
        loading = true
        errorMessage = null
        try {
            roles = runCatching { repository.loadRoles() }.getOrDefault(emptyList())
            if (guideId != null) {
                val guide = repository.loadGuide(guideId)
                title = guide.title
                description = guide.description
                blocks = guide.blocks
                hasPublishedVersion = guide.hasPublishedVersion
                isDraft = guide.status == GuideStatus.RASCUNHO
                version = guide.version
                allRoles = guide.allRoles
                selectedRoles = guide.roleIds.toSet()
            }
        } catch (exception: CancellationException) {
            throw exception
        } catch (throwable: Throwable) {
            errorMessage = throwable.message ?: "N�o foi poss�vel carregar o guia."
        } finally {
            loading = false
        }
    }

    fun updateTitle(value: String) {
        title = value
    }

    fun updateDescription(value: String) {
        description = value.take(MAX_DESCRIPTION)
    }

    fun addBlock(type: GuideBlockType) {
        blocks = blocks + GuideBlock(id = newBlockId(), type = type)
    }

    fun removeBlock(id: String) {
        blocks = blocks.filterNot { it.id == id }
        pendingMedia = pendingMedia - id
    }

    fun moveBlock(
        id: String,
        offset: Int,
    ) {
        val index = blocks.indexOfFirst { it.id == id }
        if (index < 0) return
        val target = index + offset
        if (target !in blocks.indices) return
        val next = blocks.toMutableList()
        val block = next.removeAt(index)
        next.add(target, block)
        blocks = next
    }

    fun updateCaption(
        id: String,
        value: String,
    ) {
        blocks =
            blocks.map {
                if (it.id == id) it.copy(caption = value.take(MAX_CAPTION)) else it
            }
    }

    fun stageMedia(
        blockId: String,
        bytes: ByteArray,
        contentType: String,
        fileName: String,
    ) {
        if (blocks.none { it.id == blockId }) return
        pendingMedia =
            pendingMedia +
            (blockId to PendingGuideMedia(bytes = bytes, contentType = contentType, fileName = fileName))
        errorMessage = null
    }

    fun pendingFor(blockId: String): PendingGuideMedia? = pendingMedia[blockId]

    fun uploadImage(
        bytes: ByteArray,
        contentType: String,
        onUploaded: (String) -> Unit,
    ) {
        if (uploadingImage) return
        scope.launch {
            uploadingImage = true
            errorMessage = null
            try {
                onUploaded(repository.uploadImage(bytes, contentType))
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel enviar a imagem."
            } finally {
                uploadingImage = false
            }
        }
    }

    fun deleteDraft(onDeleted: () -> Unit) {
        val id = currentGuideId ?: return
        if (deleting) return
        scope.launch {
            deleting = true
            errorMessage = null
            try {
                repository.delete(id)
                currentGuideId = null
                pendingMedia = emptyMap()
                onDeleted()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível excluir o rascunho."
            } finally {
                deleting = false
            }
        }
    }

    fun toggleAll() {
        allRoles = !allRoles
        selectedRoles = emptySet()
    }

    fun toggleRole(id: String) {
        val current = effectiveSelectedRoles
        val next = if (id in current) current - id else current + id
        val all = roles.map { it.id }.toSet()
        allRoles = all.isNotEmpty() && next == all
        selectedRoles = if (allRoles) emptySet() else next
    }

    fun save(
        textHtml: Map<String, String>,
        publish: Boolean,
        onSaved: (String) -> Unit,
    ) {
        if (!canSave) return
        val merged =
            blocks.map { block ->
                if (block.type == GuideBlockType.TEXT) {
                    block.copy(html = textHtml[block.id] ?: block.html)
                } else {
                    block
                }
            }
        val invalid =
            merged.any { block ->
                when (block.type) {
                    GuideBlockType.TEXT -> block.html.isBlank()
                    else -> block.documentId.isBlank() && pendingMedia[block.id] == null
                }
            }
        if (invalid) {
            errorMessage = "Preencha todas as se��es antes de salvar."
            return
        }
        scope.launch {
            saving = true
            errorMessage = null
            try {
                var prepared = merged
                for (block in merged) {
                    val pending = pendingMedia[block.id] ?: continue
                    val media =
                        repository.uploadMedia(
                            bytes = pending.bytes,
                            contentType = pending.contentType,
                            fileName = pending.fileName,
                            kind = block.type,
                        )
                    prepared =
                        prepared.map {
                            if (it.id == block.id) {
                                it.copy(
                                    documentId = media.id,
                                    fileName = media.fileName.ifBlank { pending.fileName },
                                )
                            } else {
                                it
                            }
                        }
                    blocks =
                        blocks.map {
                            if (it.id == block.id) {
                                it.copy(
                                    documentId = media.id,
                                    fileName = media.fileName.ifBlank { pending.fileName },
                                )
                            } else {
                                it
                            }
                        }
                    pendingMedia = pendingMedia - block.id
                }
                val draft =
                    GuideDraft(
                        title = title.trim(),
                        description = description.trim(),
                        blocks = prepared,
                        allRoles = allRoles,
                        roleIds = if (allRoles) emptyList() else selectedRoles.toList(),
                    )
                val saved = currentGuideId?.let { repository.update(it, draft) } ?: repository.create(draft)
                currentGuideId = saved.id
                val result = if (publish) repository.publish(saved.id) else saved
                blocks = result.blocks
                hasPublishedVersion = result.hasPublishedVersion
                isDraft = result.status == GuideStatus.RASCUNHO
                version = result.version
                pendingMedia = emptyMap()
                onSaved(result.id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "N�o foi poss�vel salvar o guia."
            } finally {
                saving = false
            }
        }
    }

    private fun newBlockId(): String {
        blockSequence += 1
        return "block-$blockSequence-${kotlin.random.Random.nextLong().toString(16)}"
    }

    private companion object {
        const val MAX_DESCRIPTION = 350
        const val MAX_CAPTION = 500
    }
}

@Composable
fun rememberGuidesController(): GuidesController {
    val repository = LocalAppGraph.current.guideRepository
    val scope = rememberCoroutineScope()
    return remember { GuidesController(repository, scope) }
}

@Composable
fun rememberGuideDetailController(guideId: String): GuideDetailController {
    val repository = LocalAppGraph.current.guideRepository
    val scope = rememberCoroutineScope()
    return remember(guideId) { GuideDetailController(repository, guideId, scope) }
}

@Composable
fun rememberGuideFormController(guideId: String?): GuideFormController {
    val repository = LocalAppGraph.current.guideRepository
    val scope = rememberCoroutineScope()
    return remember(guideId) { GuideFormController(repository, guideId, scope) }
}

private suspend fun exportGuidePdf(
    repository: GuideRepository,
    guide: Guide,
    assets: GuidePdfAssets,
) {
    val year = SystemDateTimeProvider().today().year
    val labels =
        GuidePdfLabels(
            version = guide.version?.let { "v$it" } ?: "Rascunho",
            slogan = "Plataforma integrada da V.V. Refeições",
            rights = "© $year. Todos os direitos reservados.",
            availableAt = "Disponível em:",
            videoMedia = "Este vídeo está disponível apenas na versão online do manual, no Portal VV.",
            pdfMedia = "Este PDF está disponível apenas na versão online do manual, no Portal VV.",
        )
    val images = mutableMapOf<String, PdfImage>()
    guide.blocks
        .filter { it.type == GuideBlockType.IMAGE && it.documentId.isNotBlank() }
        .forEach { block ->
            runCatching {
                val bitmap = repository.loadMedia(block.documentId).decodeToImageBitmap()
                val scale = min(1f, 600f / bitmap.width.coerceAtLeast(1))
                BitmapPainter(bitmap).toPdfImage(
                    widthPx = (bitmap.width * scale).toInt().coerceAtLeast(1),
                    heightPx = (bitmap.height * scale).toInt().coerceAtLeast(1),
                )
            }.onSuccess { images[block.documentId] = it }
        }
    val document =
        buildGuidePdf(
            guide = guide,
            labels = labels,
            assets = assets,
            shareLink = guideShareLink(guide.id),
            images = images,
        )
    savePdf(pdfFileName(guide.title), encodeBase64(PdfWriter.build(document)))
}

private fun pdfFileName(title: String): String {
    val base = title.trim().ifBlank { "manual" }
    val sanitized = base.map { if (it.isLetterOrDigit() || it == '-' || it == '_' || it == '.') it else '_' }.joinToString("")
    return "$sanitized.pdf"
}
